import { useCallback, useState } from "react";
import { omit } from "lodash";
import { useDispatch } from "react-redux";
import { useLocation } from "wouter";
import {
  investmentsApi,
  useCreateMarketUpdateMutation,
  useCreateTransactionMutation,
  useDeleteInvestmentMutation,
  useLazyGetAccountDeletionImpactQuery,
  useUpdateInvestmentAccountStatusMutation,
  useUpdateInvestmentMutation,
  useUpdateMarketUpdateMutation,
  useUpdateTransactionMutation,
  DeletionImpact,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { getErrorMessages } from "../../../../services/errors";
import { formatDate } from "../../../../utils";
import {
  CreateTransactionsObject,
  TransactionTypeOptions,
} from "../../../Transactions";
import { UseInvestmentActionsProps, UseInvestmentActionsReturn } from "./types";
import { InvestmentFormValues, MarketUpdate, ProgressType } from "../../panels";
import { UpdateInvestmentPayload } from "../../types";
import {
  isLedgerMode,
  settlementAccountOptionOf,
  settlementPayloadOf,
} from "../../utils";

// `catch` blocks here exist only for UI control flow — the global error middleware raises the toast. The
// contribution branch of `handleSubmit` also dispatches a slim per-id invalidation the generic ripple can't know about.
export const useInvestmentActions = ({
  id,
  investment,
  investmentTypes,
  onSuccess,
  onEdit,
  onUpdated,
  panelAction,
  panelProgress,
}: UseInvestmentActionsProps): UseInvestmentActionsReturn => {
  const [, setLocation] = useLocation();
  const dispatch = useDispatch();
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletionImpact, setDeletionImpact] = useState<DeletionImpact | null>(
    null,
  );

  const [deleteInvestmentTrigger, { isLoading: isDeleting }] =
    useDeleteInvestmentMutation();
  const [updateInvestmentAccountStatus] =
    useUpdateInvestmentAccountStatusMutation();
  const [updateInvestmentTrigger] = useUpdateInvestmentMutation();
  const [createMarketUpdate] = useCreateMarketUpdateMutation();
  const [updateMarketUpdate] = useUpdateMarketUpdateMutation();
  const [fetchImpact] = useLazyGetAccountDeletionImpactQuery();
  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();

  const resetDeleteError = useCallback(() => setDeleteError(null), []);

  const fetchDeletionImpact = useCallback(async () => {
    setIsLoadingImpact(true);
    try {
      const impact = await fetchImpact(investment.accountId).unwrap();
      setDeletionImpact(impact);
    } catch {
      // If we can't fetch impact, allow deletion with fallback to static warning.
      // The global middleware shows the toast.
      setDeletionImpact(null);
    } finally {
      setIsLoadingImpact(false);
    }
  }, [fetchImpact, investment.accountId]);

  const handleEdit = useCallback(() => {
    const investmentTypeOption = investmentTypes.find(
      (type) => type.value === String(investment.investmentTypeId),
    );
    if (!investmentTypeOption) {
      notify.error("Investment type not found");
      return;
    }
    onEdit({
      id: investment.id,
      name: investment.name,
      institution: investment.institution,
      investmentType: investmentTypeOption,
      startDate: new Date(investment.startDate),
      cashSettlement: investment.cashSettlement ?? "OWN_CASH",
      settlementAccount: settlementAccountOptionOf(investment),
    });
  }, [investment, investmentTypes, onEdit]);

  const settlementChangeOf = useCallback(
    (values: InvestmentFormValues) => {
      const isEditable =
        isLedgerMode(investment.trackingMode) && !investment.hasLedgerCommands;
      if (!isEditable) {
        return undefined;
      }
      const next = settlementPayloadOf(values);
      const isUnchanged =
        next.cashSettlement === (investment.cashSettlement ?? "OWN_CASH") &&
        (next.settlementAccountId ?? null) === investment.settlementAccountId;
      return isUnchanged ? undefined : next;
    },
    [investment],
  );

  const handleUpdateInvestment = useCallback(
    async (values: InvestmentFormValues) => {
      const payload: UpdateInvestmentPayload = {
        name: values.name,
        institution: values.institution,
        investmentType: values.investmentType
          ? Number(values.investmentType.value)
          : undefined,
        startDate: formatDate(values.startDate),
        ...settlementChangeOf(values),
      };
      try {
        await updateInvestmentTrigger({ id, payload }).unwrap();
        notify.success("Investment updated");
        onUpdated();
      } catch {
        // Global middleware shows the toast for mutation failures.
      }
    },
    [id, updateInvestmentTrigger, onUpdated, settlementChangeOf],
  );

  const handleOnDelete = useCallback(async () => {
    setDeleteError(null);
    try {
      await deleteInvestmentTrigger(id).unwrap();
      notify.success("Investment Deleted");
      setLocation("/investments");
    } catch (err: any) {
      // Keep the modal open with an inline error so the user can retry.
      setDeleteError(getErrorMessages(err).join(" "));
    }
  }, [id, deleteInvestmentTrigger, setLocation]);

  const handleSubmit = useCallback(
    async (data: ProgressType) => {
      if (data.transactionType.value === TransactionTypeOptions.MARKET_UPDATE) {
        const marketUpdate: MarketUpdate = {
          amount: Number(data.amount),
          date: formatDate(data.transactionDate),
        };
        try {
          if (panelAction === "edit") {
            await updateMarketUpdate({
              id,
              marketUpdateId: panelProgress!.id!,
              payload: marketUpdate,
            }).unwrap();
            notify.success("Market Update edited successfully!");
          } else {
            await createMarketUpdate({ id, payload: marketUpdate }).unwrap();
            notify.success("Market Update created successfully!");
          }
          onSuccess();
        } catch {
          // Global middleware shows the toast for mutation failures.
        }
        return;
      }

      const payload: CreateTransactionsObject = {
        ...omit(data, "id"),
        transactionDate: formatDate(data.transactionDate),
        category: null,
        subCategory: null,
        isRecurring: false,
        frequency: null,
      };
      try {
        if (panelAction === "edit") {
          await updateTransaction({
            transactionId: panelProgress!.id!,
            transaction: payload,
          }).unwrap();
          notify.success("Transaction edited successfully!");
        } else {
          await createTransaction(payload).unwrap();
          notify.success("Transaction created successfully!");
        }
        // The transaction ripple covers history and `Finances`, but it cannot
        // know this write was a contribution to investment `id`.
        dispatch(
          investmentsApi.util.invalidateTags([
            { type: "InvestmentTransfer", id },
            { type: "Investment", id },
          ]),
        );
        onSuccess();
      } catch {
        // Global middleware shows the toast for mutation failures.
      }
    },
    [
      id,
      panelAction,
      panelProgress,
      createMarketUpdate,
      updateMarketUpdate,
      createTransaction,
      updateTransaction,
      dispatch,
      onSuccess,
    ],
  );

  const handleOnStatusChangeClick = useCallback(async () => {
    const message = investment.isActive
      ? "Investment deactivated"
      : "Investment reactivated";
    try {
      await updateInvestmentAccountStatus({
        id,
        isActive: !investment.isActive,
      }).unwrap();
      notify.success(message);
      onSuccess();
    } catch {
      // Global middleware shows the toast.
    }
  }, [id, investment.isActive, updateInvestmentAccountStatus, onSuccess]);

  return {
    handleEdit,
    handleUpdateInvestment,
    handleOnDelete,
    handleSubmit,
    handleOnStatusChangeClick,
    isLoadingImpact,
    isDeleting,
    deleteError,
    deletionImpact,
    fetchDeletionImpact,
    resetDeleteError,
  };
};
